import React, { useState, useEffect } from 'react';
import CreateTemplateModal from './CreateTemplateModal';
import ApplyTemplateModal from './ApplyTemplateModal';
import { shiftTemplateService } from './shiftTemplateService';
import Button from 'components/GeneralComponents/Button';
import Modal from 'components/GeneralComponents/Modal';
import { connect } from 'react-redux';
import './ShiftTemplatesModal.scss';
import GenericConfirmActionModal from 'pages/CommunityMergeFeed/CreateFeed/GenericConfirmActionModal';

const ShiftTemplatesList = (props) => {
  const { locationId, channels } = props;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [applyModalVisible, setApplyModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [search, setSearch] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, [locationId]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await shiftTemplateService.getTemplates(locationId);
      setTemplates(response.shift_templates || []);
    } catch (error) {
      showToast('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 2000);
  };

  const handleDelete = async (templateId) => {
    try {
      await shiftTemplateService.deleteTemplate(templateId);
      showToast('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      showToast('Failed to delete template', 'error');
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditModalVisible(true);
  };

  const handleApply = (template) => {
    setSelectedTemplate(template);
    setApplyModalVisible(true);
  };

  const openDeleteModal = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    await handleDelete(templateToDelete.id);
    closeDeleteModal();
  };

  // Add timing for Create Template button click
  const handleCreateTemplateClick = () => {
    console.time('Create Template Button Click to Modal Open');
    setCreateModalVisible(true);
    // Use setTimeout to measure when the modal actually becomes visible
    setTimeout(() => {
      console.timeEnd('Create Template Button Click to Modal Open');
    }, 0);
  };

  // Track when createModalVisible changes
  useEffect(() => {
    if (createModalVisible) {
      console.time('Modal State Change to Render');
      // Use requestAnimationFrame to measure when the modal actually renders
      requestAnimationFrame(() => {
        console.timeEnd('Modal State Change to Render');
      });
    }
  }, [createModalVisible]);

  // Filter templates by search
  const filteredTemplates = templates.filter(t => {
    const q = search.toLowerCase();
    return (
      t.name?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      (Array.isArray(t.tags) && t.tags.some(tag => tag.toLowerCase().includes(q)))
    );
  });

  return (
    <div className="shift-templates-list__wrap">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Shift Templates</h2>
        <Button action={handleCreateTemplateClick} text="Create Template" type="primary" />
      </div>
      <div style={{ marginBottom: 24 }}>
        <input
          type="text"
          className="template-search-bar"
          placeholder="Search templates by name, description, or tags..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', maxWidth: 480, padding: '12px 18px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 16 }}
        />
      </div>
      <div className="template-cards-grid">
        {loading ? (
          <div>Loading...</div>
        ) : filteredTemplates.length === 0 ? (
          <div>No templates found.</div>
        ) : filteredTemplates.map(record => (
          <div key={record.id} className="template-card">
            <div className="template-card-header">
              <div>
                <div className="template-card-title">{record.name}</div>
                <div className="template-card-desc">{record.description}</div>
              </div>
              <div className="template-card-public">
                <span role="img" aria-label="public">👥</span> Public
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
              <span style={{ fontSize: 15 }}><span role="img" aria-label="calendar">📅</span> {record.shift_template_shifts?.length || 0} shifts</span>
              <div className="template-card-tags">
                {Array.isArray(record.tags) && record.tags.map(tag => (
                  <span key={tag} className="template-card-tag">{tag}</span>
                ))}
              </div>
            </div>
            <div className="template-card-shifts-label">Shifts:</div>
            <div className="template-card-shift-list">
              {record.shift_template_shifts?.map((shift, idx) => (
                <div key={idx} className="template-card-shift-row">
                  {shift.start_time} - {shift.end_time} • {shift.location || shift.group?.channel_name || '-'} • {shift.position || '-'}
                </div>
              ))}
            </div>
            <div className="template-card-actions">
              <button className="template-btn secondary" onClick={() => handleEdit(record)} title="Edit"><span role="img" aria-label="edit">✏️</span></button>
              <button className="template-btn primary" onClick={() => handleApply(record)}>APPLY TEMPLATE</button>
              <button className="template-btn danger" onClick={() => openDeleteModal(record)} title="Delete"><span role="img" aria-label="delete">🗑️</span></button>
            </div>
          </div>
        ))}
      </div>
      {toast.show && <div className={`toast-message ${toast.type}`}>{toast.message}</div>}
      {createModalVisible && (
        <Modal>
          <div className="shift-templates-modal-overlay">
            <div className="shift-templates-modal-content">
              <CreateTemplateModal
                visible={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                onSuccess={() => {
                  setCreateModalVisible(false);
                  loadTemplates();
                  showToast('Template created successfully');
                }}
                locationId={locationId}
              />
            </div>
          </div>
        </Modal>
      )}
      {editModalVisible && (
        <Modal>
          <div className="shift-templates-modal-overlay">
            <div className="shift-templates-modal-content">
              <CreateTemplateModal
                visible={editModalVisible}
                template={selectedTemplate}
                onCancel={() => {
                  setEditModalVisible(false);
                  setSelectedTemplate(null);
                }}
                onSuccess={() => {
                  setEditModalVisible(false);
                  setSelectedTemplate(null);
                  loadTemplates();
                  showToast('Template updated successfully');
                }}
                locationId={locationId}
              />
            </div>
          </div>
        </Modal>
      )}
      {applyModalVisible && (
        <Modal>
          <div className="shift-templates-modal-overlay">
            <div className="shift-templates-modal-content">
              <ApplyTemplateModal
                visible={applyModalVisible}
                template={selectedTemplate}
                channels={channels}
                onCancel={() => {
                  setApplyModalVisible(false);
                  setSelectedTemplate(null);
                }}
                onSuccess={() => {
                  setApplyModalVisible(false);
                  setSelectedTemplate(null);
                  showToast('Template applied successfully');
                }}
              />
            </div>
          </div>
        </Modal>
      )}
      {showDeleteModal && (
        <GenericConfirmActionModal
          title="Delete Template"
          message="Are you sure you want to delete this template? This action cannot be undone."
          confirmLabel="Delete"
          actionType="delete-group"
          onCancel={closeDeleteModal}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  locationId: state.locationReducer.location_code,
  channels: state.appReducer.channels.data,
});

export default connect(mapStateToProps)(ShiftTemplatesList); 